import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { getContributionComments, submitComment } from '../../api/comments'

type ObjectType = 'contribution' | 'contribution_comment' | 'contribution_material'
type TargetKey = string // `${object_type}:${object_id}`

export const fetchContributionComments = createAsyncThunk(
    'comments/fetchContributionComments',
    async (params: { object_type: ObjectType; object_id: number; offset?: number; limit?: number }) => {
        return {
            key: `${params.object_type}:${params.object_id}` as TargetKey,
            data: await getContributionComments({
                ...params,
                offset: params.offset ?? 0,
                limit: params.limit ?? 10
            }),
            offset: params.offset ?? 0,
            limit: params.limit ?? 10
        }
    }
)

export const sendComment = createAsyncThunk(
    'comments/sendComment',
    async (
        params: {
            object_type: ObjectType
            object_id: number
            content: string
            image_data?: string
        },
        { rejectWithValue }
    ) => {
        try {
            const res = await submitComment(params)
            return {
                key: `${params.object_type}:${params.object_id}` as TargetKey,
                item: res.item
            }
        } catch (err: any) {
            return rejectWithValue(err.message ?? 'Error submitting comment')
        }
    }
)

interface CommentsForTarget {
    list: any[]
    loading: boolean
    error: string | null
    hasMore: boolean
    offset: number
    limit: number
    key: number | string
}

interface CommentsState {
    byTarget: Record<TargetKey, CommentsForTarget>
}

const initialState: CommentsState = {
    byTarget: {}
}

const commentsSlice = createSlice({
    name: 'comments',
    initialState,
    reducers: {
        clearComments(state, action: PayloadAction<{ object_type: ObjectType; object_id: number }>) {
            const key = `${action.payload.object_type}:${action.payload.object_id}`
            delete state.byTarget[key]
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchContributionComments.pending, (state, action) => {
                const { object_type, object_id, offset, limit } = action.meta.arg
                const key = `${object_type}:${object_id}`

                if (!state.byTarget[key]) {
                    state.byTarget[key] = {
                        list: [],
                        loading: true,
                        error: null,
                        hasMore: true,
                        key: key,
                        offset: offset ?? 0,
                        limit: limit ?? 10
                    }
                } else {
                    state.byTarget[key].loading = true
                    state.byTarget[key].error = null
                }
            })
            .addCase(fetchContributionComments.fulfilled, (state, action) => {
                const { key, data, offset, limit } = action.payload
                const target = state.byTarget[key]

                if (!target) return

                target.loading = false
                target.error = null

                if (data.list.length === 0) {
                    target.hasMore = false
                    return
                }

                if (offset === 0) {
                    target.list = data.list
                } else {
                    target.list = [...target.list, ...data.list]
                }

                target.offset = offset + limit
                target.limit = limit
                target.hasMore = data.list.length >= limit
                // console.log(action)
                target.key = action.payload.key
            })
            .addCase(fetchContributionComments.rejected, (state, action) => {
                const { object_type, object_id } = action.meta.arg
                const key = `${object_type}:${object_id}`
                if (!state.byTarget[key]) return
                state.byTarget[key].loading = false
                state.byTarget[key].error = action.error.message ?? 'Error comments loading'
            })
            .addCase(sendComment.fulfilled, (state, action) => {
                const { key, item } = action.payload
                const target = state.byTarget[key]

                if (!target) {
                    state.byTarget[key] = {
                        list: [item],
                        loading: false,
                        error: null,
                        hasMore: true,
                        key,
                        offset: 1,
                        limit: 10
                    }
                } else {
                    target.list = [item, ...target.list]
                }
            })
            .addCase(sendComment.rejected, (state, action) => {
                const { object_type, object_id } = action.meta.arg
                const key = `${object_type}:${object_id}`
                if (!state.byTarget[key]) return
                state.byTarget[key].error =
                    (action.payload as string) ?? action.error.message ?? 'Error sending comment'
            })
    }
})

export const { clearComments } = commentsSlice.actions
export default commentsSlice.reducer
